import { AlertTriangle, GitCompare, Undo2 } from "lucide-react";
import React from "react";

import type { OzonChange, OzonChangePlan } from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "../format";
import {
  STEP_LABEL,
  applying,
  changeSize,
  editing,
  failed,
  staleOf,
  type CascadeStep,
} from "./changeModel";

/** Янтарная полоса поверх страницы: правка идёт, и всё, что напечатано и записано, — СТАРОЕ.
 *
 *  ⚠️ Полоса, а не значок: пока правка не применена, состав у площадки, строки в 1С и наклейки на
 *  коробах показывают прежнюю поставку. Человек, зашедший на страницу из списка, обязан прочитать
 *  это раньше, чем нажмёт «печать» — печать в это время погашена с той же причиной (`ozonStage`).
 */
export function ChangeModeBar({
  change,
  plan,
  busy,
  onPreview,
  onAbort,
}: {
  change: OzonChange;
  /** план каскада; `null` — превью ещё не считали или оно не сошлось */
  plan: OzonChangePlan | null;
  busy: boolean;
  /** раскрыть раздел «Что изменится» */
  onPreview: () => void;
  onAbort: () => void;
}): React.ReactElement {
  const stale = staleOf(plan);
  const size = changeSize(plan);
  const what = [
    stale.ozon ? "площадка" : "",
    stale.onec ? "1С" : "",
    stale.labels ? "этикетки" : "",
  ].filter(Boolean);
  return (
    <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <b className="flex items-center gap-1.5">
          <GitCompare className="size-4 shrink-0" /> Режим изменений состава
        </b>
        <span className="text-xs">
          №{change.id} · {change.created_by} · {fmtDateTime(change.created_at)}
        </span>
        {/* ⚠️ Правки считаем ПО ПЛАНУ, а не по числу нажатий: три переноса туда-обратно — ноль
            правок, и счётчик кликов врал бы ровно там, где человек проверяет себя */}
        <span className="text-xs">
          {size === null
            ? "правок: превью ещё не считано"
            : size.total === 0
              ? "правок нет: раскладка совпадает с отправленной"
              : `правок: строк состава ${size.content} · мест ${size.places}`}
        </span>
        {what.length > 0 && (
          <span className="text-xs font-medium">
            {what.join(" / ")} {what.length > 1 ? "устарели" : "устарело"} до
            применения
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onPreview}
            title="Дельта по площадке, 1С и печати — до того, как что-то уедет наружу"
          >
            <GitCompare className="size-4" /> Показать, что изменится
          </Button>
          {/* ⚠️ Отмена только пока заход в правке: дальше каскад уже сходил наружу, и «вернуть
              как было» у себя значило бы соврать про площадку и 1С */}
          {editing(change) && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={onAbort}
              title="Вернуть раскладку такой, какой она уехала на площадку"
            >
              <Undo2 className="size-4" /> Отменить изменения
            </Button>
          )}
        </div>
      </div>

      {/* Площадка НАЗВАЛА отказ на шаге состава — единственный откат каскада назад, и причину
          человек обязан прочитать: править дальше можно, но состав площадка не приняла */}
      {editing(change) && change.error && (
        <div className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span>Площадка отказала: {change.error}</span>
        </div>
      )}
      {applying(change) && (
        <div className="text-xs">
          Идёт применение — шаг «{STEP_LABEL[change.state as CascadeStep]}».
          Раскладка сейчас не правится: каскад работает по сохранённому плану.
        </div>
      )}
      {failed(change) && (
        <div className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="size-3.5 shrink-0" />
          <span>
            Каскад встал на шаге «{STEP_LABEL[change.state as CascadeStep]}»:{" "}
            {change.error} — продолжите применение, он возобновится с этого же
            шага.
          </span>
        </div>
      )}
    </div>
  );
}
